import { createStore, combineReducers, applyMiddleware } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { composeWithDevTools } from 'redux-devtools-extension';
import tokenMiddleware from './tokenMiddleware';
import { all } from 'redux-saga/effects';
import { PersistConfig, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { setAutoFreeze } from 'immer';

import { KEY as AUTH, reducer as authReducer, sagas as authSagas } from './auth';
import { KEY as CLIENTS, reducer as clientsReducer, sagas as clientsSagas } from './clients';
import { KEY as KIOSKS, reducer as kiosksReducer, sagas as kiosksSagas } from './kiosks';
import { KEY as ADMINS, reducer as adminsReducer, sagas as adminsSagas } from './admins';
import { KEY as ACTIVITY_LOG, reducer as activityLogReducer, sagas as activityLogSagas } from './activityLog';
import { KEY as DELIVERY_LOG, reducer as deliveryLogReducer, sagas as deliveryLogSagas } from './deliveryLog';
import { KEY as MEDIA, reducer as mediaReducer, sagas as mediaSagas } from './media';
import { KEY as SETTINGS, reducer as settingsReducer, sagas as settingsSagas } from './settings';
import { KEY as VENUES, reducer as venuesReducer, sagas as venuesSagas } from './venues';
import { KEY as CLIENT_INTEGRATIONS, reducer as ciReducer, sagas as ciSagas } from './clientIntegrations';
import { KEY as HARDWARE, reducer as hardwareReducer, sagas as hardwareSagas } from './hardware';
import { KEY as KIOSK_HARDWARE, reducer as kioskHardwareReducer, sagas as kioskHardwareSagas } from './kioskHardware';
import {
  KEY as KIOSK_TICKET_TEMPLATES,
  reducer as kioskTicketTemplateReducer,
  sagas as kioskTicketTemplateSagas
} from './kioskTicketTemplates';
import {
  KEY as CONFIGURATIONS,
  reducer as configurationsReducer,
  sagas as configurationsSagas
} from './configurations';
import { KEY as AREAS, reducer as AreasReducer, sagas as AreasSagas } from './areas';
import { KEY as INTEGRATIONS, reducer as integrationsReducer, sagas as integrationsSagas } from './integrations';
import {
  KEY as TICKET_TEMPLATES,
  reducer as ticketTemplateReducer,
  sagas as ticketTemplateSagas
} from './ticketTemplates';

const composeEnhancers = composeWithDevTools({});
const sagaMiddleware = createSagaMiddleware();
setAutoFreeze(false);

// Place sagas to run here
const sagas = function* createSagas() {
  yield all([
    ...authSagas,
    ...clientsSagas,
    ...kiosksSagas,
    ...adminsSagas,
    ...activityLogSagas,
    ...deliveryLogSagas,
    ...mediaSagas,
    ...settingsSagas,
    ...configurationsSagas,
    ...ciSagas,
    ...venuesSagas,
    ...AreasSagas,
    ...integrationsSagas,
    ...hardwareSagas,
    ...kioskHardwareSagas,
    ...ticketTemplateSagas,
    ...kioskTicketTemplateSagas
  ]);
};

const authPersistConfig: PersistConfig = {
  key: AUTH,
  storage,
  whitelist: ['token', 'admin']
};

const clientsPersistConfig: PersistConfig = {
  key: CLIENTS,
  storage,
  whitelist: ['clientId', 'clients']
};

const kioskPersistConfig: PersistConfig = {
  key: KIOSKS,
  storage,
  whitelist: ['autoRefresh']
};

// Place reducers to run here
const reducers = combineReducers({
  [AUTH]: persistReducer(authPersistConfig, authReducer),
  [CLIENTS]: persistReducer(clientsPersistConfig, clientsReducer),
  [KIOSKS]: persistReducer(kioskPersistConfig, kiosksReducer),
  [ADMINS]: adminsReducer,
  [ACTIVITY_LOG]: activityLogReducer,
  [DELIVERY_LOG]: deliveryLogReducer,
  [MEDIA]: mediaReducer,
  [SETTINGS]: settingsReducer,
  [CONFIGURATIONS]: configurationsReducer,
  [CLIENT_INTEGRATIONS]: ciReducer,
  [VENUES]: venuesReducer,
  [AREAS]: AreasReducer,
  [INTEGRATIONS]: integrationsReducer,
  [HARDWARE]: hardwareReducer,
  [KIOSK_HARDWARE]: kioskHardwareReducer,
  [TICKET_TEMPLATES]: ticketTemplateReducer,
  [KIOSK_TICKET_TEMPLATES]: kioskTicketTemplateReducer
});

export const store = createStore(reducers, composeEnhancers(applyMiddleware(sagaMiddleware, tokenMiddleware)));
export const persistor = persistStore(store);

sagaMiddleware.run(sagas);
